import { Crosshair, Loader2, MapPin, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useLocationAutocomplete } from "@/api/hooks";
import type { SearchLocationMode } from "@/api/types";
import { LocationAttribution } from "@/components/forms/location-attribution";
import { DEBOUNCE_MS } from "@/features/search/types";
import { useDebounce } from "@/hooks/use-debounce";
import { useGeolocation } from "@/hooks/use-geolocation";
import { formatLocationLabel } from "@/lib/format-location-label";
import { resolveLocationLabel } from "@/lib/resolve-location-label";

interface WherePanelProps {
  value: string | null;
  onChange: (
    location: string | null,
    lat: number | null,
    lng: number | null,
    bounds: string | null,
    locationMode: SearchLocationMode | null,
  ) => void;
}

// fallow-ignore-next-line complexity
function WherePanel({ value, onChange }: WherePanelProps) {
  const [input, setInput] = useState(value ?? "");
  const inputRef = useRef<HTMLInputElement>(null);
  const debouncedQuery = useDebounce(input, DEBOUNCE_MS);
  const { data, isLoading, isError, isFetching, refetch } = useLocationAutocomplete(debouncedQuery);
  const searchBusy = isLoading || input !== debouncedQuery;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const geo = useGeolocation();
  const cancelGeolocation = geo.cancel;
  const nearMeRequest = useRef<AbortController | null>(null);
  const [resolvingNearMe, setResolvingNearMe] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const nearMeBusy = geo.loading || resolvingNearMe;

  useEffect(() => {
    cancelGeolocation();
    nearMeRequest.current?.abort();
    nearMeRequest.current = null;
    setResolvingNearMe(false);
    setLookupError(null);
    setInput(value ?? "");
  }, [value, cancelGeolocation]);

  useEffect(() => () => nearMeRequest.current?.abort(), []);

  function cancelNearMe() {
    cancelGeolocation();
    nearMeRequest.current?.abort();
    nearMeRequest.current = null;
    setResolvingNearMe(false);
    setLookupError(null);
  }

  function handleNearMe() {
    cancelNearMe();
    const request = new AbortController();
    nearMeRequest.current = request;
    geo.request((lat, lng) => {
      if (request.signal.aborted) return;
      void (async () => {
        setResolvingNearMe(true);
        try {
          const display = await resolveLocationLabel(lat, lng, request.signal);
          if (!request.signal.aborted) onChangeRef.current(display, lat, lng, null, "coords");
        } catch (error) {
          if (
            request.signal.aborted ||
            (error instanceof DOMException && error.name === "AbortError")
          )
            return;
          setLookupError(
            "Unable to look up your location. Try Near me again or search for an area.",
          );
        } finally {
          if (!request.signal.aborted) {
            nearMeRequest.current = null;
            setResolvingNearMe(false);
          }
        }
      })();
    });
  }

  const handleSelect = (
    name: string,
    region: string | null,
    lat: number,
    lng: number,
    bounds: string | null,
  ) => {
    cancelNearMe();
    onChange(formatLocationLabel(name, region), lat, lng, bounds, "place");
  };

  const suggestions = data?.suggestions ?? [];
  const showSuggestions = debouncedQuery.length >= 2;

  return (
    <div className="max-h-[320px] overflow-y-auto">
      {/* Search input */}
      <div className="flex items-center gap-2 border-b border-fog px-4 py-3">
        <MapPin className="size-4 shrink-0 text-slate" />
        <input
          ref={inputRef}
          type="text"
          className="flex-1 bg-transparent text-[16px] text-ink outline-none placeholder:text-slate"
          placeholder="Search location..."
          aria-label="Search location"
          value={input}
          onChange={(e) => {
            cancelNearMe();
            setInput(e.target.value);
          }}
          // biome-ignore lint/a11y/noAutofocus: panel just opened, focusing the input is expected
          autoFocus
        />
        {input && (
          <button
            type="button"
            aria-label="Clear location"
            className="shrink-0 rounded-xs p-0.5 text-slate transition-colors hover:text-ink"
            onClick={() => {
              cancelNearMe();
              setInput("");
              onChange(null, null, null, null, null);
            }}
          >
            <X className="size-3.5" />
          </button>
        )}
      </div>

      {/* Current location */}
      <button
        type="button"
        className="flex w-full items-center gap-3 px-4 py-3 text-left text-[14px] transition-colors hover:bg-fog"
        onClick={handleNearMe}
        disabled={nearMeBusy}
      >
        {nearMeBusy ? (
          <Loader2 className="size-5 shrink-0 animate-spin text-electric-800" />
        ) : (
          <span className="flex size-8 items-center justify-center rounded-lg bg-electric-50">
            <Crosshair className="size-4 text-electric-800" />
          </span>
        )}
        <span className="font-medium text-electric-800">
          {nearMeBusy ? "Locating..." : "Near me"}
        </span>
      </button>

      {(geo.error || lookupError) && (
        <p role="alert" className="px-4 pb-2 text-[13px] text-danger">
          {geo.error || lookupError}
        </p>
      )}

      {/* Loading */}
      {showSuggestions && searchBusy && (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="size-4 animate-spin text-slate" />
        </div>
      )}

      {showSuggestions && !searchBusy && isError && (
        <div role="alert" className="px-4 py-4 text-[14px] text-danger">
          Unable to load places.{" "}
          <button
            type="button"
            className="font-semibold text-electric-800 underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-electric-700"
            disabled={isFetching}
            onClick={() => void refetch()}
          >
            Try again
          </button>
        </div>
      )}

      {/* No results */}
      {showSuggestions && !searchBusy && !isError && suggestions.length === 0 && (
        <p className="px-4 py-6 text-center text-[13px] text-slate">No locations found</p>
      )}

      {/* Suggestions */}
      {showSuggestions &&
        !searchBusy &&
        !isError &&
        suggestions.map((s) => (
          <button
            key={`${s.lat}-${s.lng}-${s.name}`}
            type="button"
            className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-[14px] transition-colors hover:bg-fog"
            onClick={() => handleSelect(s.name, s.region, s.lat, s.lng, s.bounds)}
          >
            <MapPin className="size-4 shrink-0 text-slate" />
            <span>
              {s.name}
              {s.region && <span className="text-slate">, {s.region}</span>}
            </span>
          </button>
        ))}

      {showSuggestions && !searchBusy && !isError && suggestions.length > 0 && (
        <LocationAttribution />
      )}

      {/* Prompt */}
      {!showSuggestions && !input && (
        <p className="px-4 py-4 text-center text-[13px] text-slate">
          Type to search for a location
        </p>
      )}
    </div>
  );
}

export { WherePanel, type WherePanelProps };
